import type { ImpersonationStoreState, UserOverrides } from '@revelio/auth';
import { MetadataKey, type User } from '@revelio/data-access';

import type { ImpersonationSource } from './impersonation-source';
import { resolvePresetName } from './settings/impersonationModal.utils';

type ImpersonationMode = 'user' | 'preset' | 'client' | 'custom';

/**
 * The active impersonation in the terms `impersonation started` and the
 * `is_impersonating` super properties report it: whatever the store can
 * vouch for, so every field but `mode` is optional.
 */
export type ImpersonationDescription = {
  mode: ImpersonationMode;
  presetName?: string;
  /** Every override key that is set — override modes only. */
  overrideKeys?: string[];
  userEmail?: string;
  userId?: string;
  clientName?: string;
  isTrial?: boolean;
  trialType?: string;
  userRole?: string;
};

export const IMPERSONATION_SUPER_PROPERTY_KEYS = [
  'is_impersonating',
  'impersonation_mode',
  'impersonated_client_name',
  'impersonated_is_trial',
] as const;

type EventProps = Record<
  string,
  string | number | boolean | string[] | undefined
>;

const compact = (props: EventProps) =>
  Object.fromEntries(
    Object.entries(props).filter(([, value]) => value !== undefined)
  );

const metadataValue = (user: Partial<User>, key: MetadataKey) =>
  user.metadata?.find((entry) => entry?.key === key)?.value ?? undefined;

const describeUser = (user: Partial<User>): ImpersonationDescription => ({
  mode: 'user',
  userEmail: user.email ?? undefined,
  userId: user.id ?? undefined,
  clientName: user.client_name ?? undefined,
  isTrial: user.live == null ? undefined : !user.live,
  trialType: metadataValue(user, MetadataKey.TrialType),
  userRole: user.role ?? undefined,
});

/**
 * Whether the overrides turn the admin into a trial user or restart the tour.
 * Judged by value, not by which keys are present: the modal copies the
 * admin's own baseline (`isTrialUser: false`, `tourCompleted`,
 * `stepsCompleted`, `role`…) into every override set it applies, so a client
 * view carries those keys too. The tour half mirrors the modal's own
 * `shouldResetTourState`.
 */
const shapesTrialOrTour = (overrides: UserOverrides): boolean =>
  overrides.isTrialUser === true ||
  overrides.live === false ||
  overrides.trialType !== undefined ||
  overrides.expirationDate !== undefined ||
  overrides.tourCompleted === false ||
  overrides.tourInterestTalentAcquisition !== undefined ||
  overrides.tourInterestCustomData !== undefined;

const describeOverrides = (
  overrides: UserOverrides
): ImpersonationDescription => {
  const presetName = resolvePresetName(overrides);
  const mode: ImpersonationMode = (() => {
    if (presetName !== null) return 'preset';
    // A client group's view: the client plus its entitlements (tabs, datasets,
    // postings, reports, role), nothing that shapes a trial.
    if (overrides.clientName !== undefined && !shapesTrialOrTour(overrides)) {
      return 'client';
    }
    return 'custom';
  })();
  return {
    mode,
    presetName: presetName ?? undefined,
    overrideKeys: Object.entries(overrides)
      .filter(([, value]) => value !== undefined)
      .map(([key]) => key)
      .sort(),
    clientName: overrides.clientName,
    isTrial: overrides.isTrialUser,
    trialType: overrides.trialType,
    userRole: overrides.role,
  };
};

export const describeImpersonation = (
  state: ImpersonationStoreState
): ImpersonationDescription | null => {
  if (state.isImpersonating && state.impersonatedUser) {
    return describeUser(state.impersonatedUser);
  }
  if (state.overrides) {
    return describeOverrides(state.overrides);
  }
  return null;
};

/** What every event carries while the impersonation is active. */
export const impersonationSuperProperties = (
  active: ImpersonationDescription
) =>
  compact({
    is_impersonating: true,
    impersonation_mode: active.mode,
    impersonated_client_name: active.clientName,
    impersonated_is_trial: active.isTrial,
  });

/** The `impersonation started` payload. */
export const impersonationStartedProperties = (
  active: ImpersonationDescription,
  source: ImpersonationSource
) =>
  compact({
    impersonation_mode: active.mode,
    preset_name: active.presetName,
    override_keys: active.overrideKeys,
    impersonated_user_email: active.userEmail,
    impersonated_user_id: active.userId,
    impersonated_client_name: active.clientName,
    impersonated_is_trial: active.isTrial,
    impersonated_trial_type: active.trialType,
    impersonated_user_role: active.userRole,
    source,
  });
